import React from 'react';
import { Link, Navigate, NavLink, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  CircleDashed,
  LayoutDashboard,
  PauseCircle,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import { Button } from '@/components/ui/button';
import {
  accountingApi,
  type LookupBeneficiary,
  type LookupExpenseType,
  type LookupProject,
  type ProjectAdvanceRow,
  type ProjectBankStatementRow,
  type ProjectDocumentLineRow,
} from '@/lib/api/accounting';
import {
  accountingLineExpenseAmount,
  advanceToExpenseEntry,
  bankStatementToExpenseEntry,
  fetchProjectAccountingLines,
  fetchProjectAdvances,
  fetchProjectBankStatements,
} from '@/lib/projectAccountingLines';
import type { ExpenseEntry } from '@/lib/mock/expenses';
import {
  DEFAULT_PROJECT_EXPENSE_PRINT_COLUMNS,
  type ProjectExpensePrintColKey,
  saveProjectExpenseCartexPrintPayload,
} from '@/lib/projectExpenseCartexPrint';
import {
  buildVoucherNo,
  saveProjectLedgerVoucherPayload,
} from '@/lib/projectLedgerVoucherPrint';
import { amountToPersianWordsRial } from '@/lib/rialWordsFa';
import { advancesApi } from '@/lib/api/advances';
import { bsiInternetBankApi } from '@/lib/banks/bsi/api';
import { cn } from '@/lib/utils';
import { createProjectExpenseColumns } from '@/pages/project-workspace/createProjectExpenseColumns';
import EditLedgerExpenseTypeModal from '@/pages/project-workspace/EditLedgerExpenseTypeModal';
import { PROJECT_WORKSPACE_SECTIONS, parseCounterpartyKey, resolveBeneficiaryLabel } from '@/pages/project-workspace/projectWorkspaceHelpers';
import ProjectWorkspaceBeneficiaryTab from '@/pages/project-workspace/ProjectWorkspaceBeneficiaryTab';
import ProjectWorkspaceOverviewTab from '@/pages/project-workspace/ProjectWorkspaceOverviewTab';

const PRINT_FIELD_OPTIONS = [
  ['row', 'ردیف'],
  ['date', 'تاریخ'],
  ['beneficiary', 'طرف هزینه'],
  ['type', 'نوع هزینه'],
  ['payment', 'روش پرداخت'],
  ['amount', 'مبلغ'],
  ['sourceBank', 'بانک مبدا'],
  ['destinationBank', 'بانک مقصد'],
  ['attachment', 'پیوست'],
  ['description', 'شرح'],
] as const satisfies ReadonlyArray<readonly [ProjectExpensePrintColKey, string]>;

function mapPaymentMethodApi(pm: string): ExpenseEntry['paymentMethod'] {
  if (pm === 'cash') return 'نقدی';
  if (pm === 'cheque') return 'تنخواه';
  return 'بانک';
}

function projectLineToExpenseEntry(line: ProjectDocumentLineRow, projectId: number): ExpenseEntry {
  const debit = Number(line.debit ?? 0);
  const credit = Number(line.credit ?? 0);
  return {
    id: line.id,
    projectId,
    beneficiaryId: line.beneficiaryKey,
    beneficiary: line.beneficiary,
    expenseTypeId: line.expenseTypeId,
    amount: accountingLineExpenseAmount(line),
    debit,
    credit,
    dateFa: line.dateFa,
    source: 'accounting',
    documentId: line.documentId,
    docNo: line.docNo,
    accountCode: line.accountCode,
    accountTitle: line.accountTitle,
    paymentMethod: mapPaymentMethodApi(line.paymentMethod),
    description: line.description || line.accountTitle || '',
    recordingMode: 'paid',
  };
}

function parseAmountFilter(raw: string): number | null {
  const digits = String(raw || '')
    .replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
    .replace(/[^\d]/g, '');
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
}

function entryAmount(e: ExpenseEntry): number {
  const credit = Number(e.credit ?? 0);
  const debit = Number(e.debit ?? e.amount ?? 0);
  return credit > 0 ? credit : debit;
}

function filterExpenseRows(
  rows: ExpenseEntry[],
  opts: {
    filterTypeId?: string;
    filterTypeLabel?: string;
    filterBeneficiaryId?: string;
    filterDateFrom?: string;
    filterDateTo?: string;
    filterDirection?: '' | 'deposit' | 'withdrawal';
    filterAmountMin?: string;
    filterAmountMax?: string;
    filterDocNo?: string;
    filterDescription?: string;
    normalizeDate: (v: string) => string;
  },
): ExpenseEntry[] {
  const from = opts.filterDateFrom || opts.filterDateTo;
  const to = opts.filterDateTo || opts.filterDateFrom;
  const fromN = from ? opts.normalizeDate(from) : '';
  const toN = to ? opts.normalizeDate(to) : '';
  const minAmount = parseAmountFilter(opts.filterAmountMin || '');
  const maxAmount = parseAmountFilter(opts.filterAmountMax || '');
  const docNeedle = (opts.filterDocNo || '').trim();
  const descNeedle = (opts.filterDescription || '').trim();
  const typeLabelNeedle = (opts.filterTypeLabel || '').trim();

  return rows.filter((e) => {
    if (opts.filterTypeId) {
      if (!e.expenseTypeId || e.expenseTypeId !== opts.filterTypeId) return false;
    } else if (typeLabelNeedle) {
      const label =
        (e.accountTitle || '').trim() || (Number(e.credit ?? 0) > 0 ? 'واریز' : 'پرداخت');
      if (label !== typeLabelNeedle) return false;
    }
    if (opts.filterBeneficiaryId && e.beneficiaryId !== opts.filterBeneficiaryId) return false;
    if (fromN && toN) {
      const d = opts.normalizeDate(e.dateFa || '');
      if (!d || d < fromN || d > toN) return false;
    }
    if (opts.filterDirection === 'deposit' && !(Number(e.credit ?? 0) > 0)) return false;
    if (opts.filterDirection === 'withdrawal') {
      const isDeposit = Number(e.credit ?? 0) > 0;
      if (isDeposit || entryAmount(e) <= 0) return false;
    }
    const amount = entryAmount(e);
    if (minAmount != null && amount < minAmount) return false;
    if (maxAmount != null && amount > maxAmount) return false;
    if (docNeedle && String(e.docNo || '').trim() !== docNeedle) return false;
    if (descNeedle && !String(e.description || '').includes(descNeedle)) return false;
    return true;
  });
}

const ProjectDetailsPage: React.FC = () => {
  const { id, section, beneficiaryKey: beneficiaryKeyParam } = useParams<{
    id: string;
    section?: string;
    beneficiaryKey?: string;
  }>();
  const projectId = Number(id || 0);
  const beneficiaryViewKey = beneficiaryKeyParam ? decodeURIComponent(beneficiaryKeyParam) : '';
  const isBeneficiaryView = Boolean(beneficiaryViewKey);
  const [project, setProject] = React.useState<LookupProject | null>(null);
  const [accountingLines, setAccountingLines] = React.useState<ProjectDocumentLineRow[]>([]);
  const [bankStatements, setBankStatements] = React.useState<ProjectBankStatementRow[]>([]);
  const [projectAdvances, setProjectAdvances] = React.useState<ProjectAdvanceRow[]>([]);
  const [advancesError, setAdvancesError] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [linesLoaded, setLinesLoaded] = React.useState(false);
  const [expenseTypes, setExpenseTypes] = React.useState<LookupExpenseType[]>([]);
  const [beneficiaries, setBeneficiaries] = React.useState<LookupBeneficiary[]>([]);
  const [accountTitleByCode, setAccountTitleByCode] = React.useState<Record<string, string>>({});
  const [filterTypeId, setFilterTypeId] = React.useState('');
  const [filterDateFrom, setFilterDateFrom] = React.useState('');
  const [filterDateTo, setFilterDateTo] = React.useState('');
  const [filterBeneficiaryId, setFilterBeneficiaryId] = React.useState('');
  const [filterDirection, setFilterDirection] = React.useState<'' | 'deposit' | 'withdrawal'>('');
  const [filterAmountMin, setFilterAmountMin] = React.useState('');
  const [filterAmountMax, setFilterAmountMax] = React.useState('');
  const [filterTypeLabel, setFilterTypeLabel] = React.useState('');
  const [filterDocNo, setFilterDocNo] = React.useState('');
  const [filterDescription, setFilterDescription] = React.useState('');
  const [filterDateError, setFilterDateError] = React.useState('');
  const [printColumns, setPrintColumns] = React.useState<Record<ProjectExpensePrintColKey, boolean>>(
    DEFAULT_PROJECT_EXPENSE_PRINT_COLUMNS,
  );
  const [editingExpenseTypeEntry, setEditingExpenseTypeEntry] = React.useState<ExpenseEntry | null>(null);
  const [savingExpenseType, setSavingExpenseType] = React.useState(false);
  const [expenseTypeEditError, setExpenseTypeEditError] = React.useState<string | null>(null);

  const reloadLedgerData = React.useCallback(async () => {
    if (!projectId) return;
    const [advances, bankRows, lines] = await Promise.all([
      fetchProjectAdvances(projectId).catch((err) => {
        setAdvancesError(err instanceof Error ? err.message : 'خطا در بارگذاری مساعده‌ها');
        return [] as ProjectAdvanceRow[];
      }),
      fetchProjectBankStatements(projectId),
      fetchProjectAccountingLines(projectId),
    ]);
    setProjectAdvances(advances);
    setBankStatements(bankRows);
    setAccountingLines(lines);
  }, [projectId]);

  React.useEffect(() => {
    if (!projectId) return;
    let mounted = true;
    setLoading(true);
    setLoadError('');
    setLinesLoaded(false);
    setProjectAdvances([]);
    setBankStatements([]);
    setAccountingLines([]);
    setAdvancesError('');

    const loadPage = async () => {
      try {
        const [projectDetail, types, advances, bankRows, lines] = await Promise.all([
          accountingApi.project(projectId),
          accountingApi.expenseTypes().catch(() => [] as LookupExpenseType[]),
          fetchProjectAdvances(projectId).catch((err) => {
            if (mounted) {
              setAdvancesError(err instanceof Error ? err.message : 'خطا در بارگذاری مساعده‌ها');
            }
            return [] as ProjectAdvanceRow[];
          }),
          fetchProjectBankStatements(projectId),
          fetchProjectAccountingLines(projectId),
        ]);
        if (!mounted) return;
        setProject(projectDetail);
        setExpenseTypes((types || []).filter((t) => t.is_active !== false));
        setBeneficiaries([]);
        setAccountTitleByCode({});
        setProjectAdvances(advances);
        setBankStatements(bankRows);
        setAccountingLines(lines);
        setLinesLoaded(true);
      } catch (err) {
        if (!mounted) return;
        setLoadError(err instanceof Error ? err.message : 'بارگذاری اطلاعات پروژه ناموفق بود.');
        try {
          const resolved = await accountingApi.project(projectId);
          if (mounted) setProject(resolved);
        } catch {
          if (mounted) setProject(null);
        }
        setLinesLoaded(true);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void loadPage();

    return () => {
      mounted = false;
    };
  }, [projectId]);

  const canEditLedgerExpenseType = React.useCallback((entry: ExpenseEntry) => {
    const id = String(entry.id || '');
    if (id.startsWith('bsi-') || id.startsWith('advance-')) return true;
    return false;
  }, []);

  const saveLedgerExpenseType = React.useCallback(
    async (expenseTypeId: string) => {
      if (!editingExpenseTypeEntry) return;
      const entry = editingExpenseTypeEntry;
      const entryId = String(entry.id || '');
      const nextTypeId = expenseTypeId.trim() ? Number(expenseTypeId) : null;
      if (expenseTypeId.trim() && !Number.isFinite(nextTypeId)) {
        setExpenseTypeEditError('نوع هزینه معتبر نیست.');
        return;
      }

      setSavingExpenseType(true);
      setExpenseTypeEditError(null);
      try {
        if (entryId.startsWith('bsi-')) {
          const registrationId = Number(entryId.replace(/^bsi-/, ''));
          if (!Number.isFinite(registrationId) || registrationId <= 0) {
            throw new Error('شناسه ثبت بانکی نامعتبر است.');
          }
          await bsiInternetBankApi.updateStatementRegistrationExpenseType(registrationId, nextTypeId);
        } else if (entryId.startsWith('advance-')) {
          const advanceId = Number(entryId.replace(/^advance-/, ''));
          const advance = projectAdvances.find((a) => a.id === advanceId);
          if (!advance) throw new Error('ردیف مساعده یافت نشد.');
          if (advance.bsi_statement_registration_id) {
            await bsiInternetBankApi.updateStatementRegistrationExpenseType(
              Number(advance.bsi_statement_registration_id),
              nextTypeId,
            );
          } else {
            await advancesApi.updateExpenseType(advance.employee_id, advance.id, nextTypeId);
          }
        } else {
          throw new Error('ویرایش نوع هزینه برای این ردیف پشتیبانی نمی‌شود.');
        }
        await reloadLedgerData();
        setEditingExpenseTypeEntry(null);
      } catch (err) {
        setExpenseTypeEditError(err instanceof Error ? err.message : 'ذخیره نوع هزینه ناموفق بود.');
      } finally {
        setSavingExpenseType(false);
      }
    },
    [editingExpenseTypeEntry, projectAdvances, reloadLedgerData],
  );

  const beneficiaryNameByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    beneficiaries.forEach((b) => map.set(b.id, b.name));
    return map;
  }, [beneficiaries]);

  const enrichExpenseEntry = React.useCallback(
    (entry: ExpenseEntry): ExpenseEntry => {
      const key = entry.beneficiaryId || '';
      return {
        ...entry,
        beneficiaryId: key,
        beneficiary: resolveBeneficiaryLabel(key, entry.beneficiary, beneficiaryNameByKey),
      };
    },
    [beneficiaryNameByKey],
  );

  const normalizeDate = React.useCallback((v: string) => {
    const en = v.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));
    return en.replace(/\D/g, '');
  }, []);

  const accountingExpenses = React.useMemo(
    () => accountingLines.map((line) => enrichExpenseEntry(projectLineToExpenseEntry(line, projectId))),
    [accountingLines, projectId, enrichExpenseEntry],
  );

  const bankExpenses = React.useMemo(
    () => bankStatements.map((row) => enrichExpenseEntry(bankStatementToExpenseEntry(row, projectId))),
    [bankStatements, projectId, enrichExpenseEntry],
  );

  const advanceExpenses = React.useMemo(
    () => projectAdvances.map((row) => enrichExpenseEntry(advanceToExpenseEntry(row, projectId))),
    [projectAdvances, projectId, enrichExpenseEntry],
  );

  const syncedBsiRegistrationIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const row of projectAdvances) {
      if (row.bsi_statement_registration_id) ids.add(Number(row.bsi_statement_registration_id));
    }
    return ids;
  }, [projectAdvances]);

  const isEmployeeBankDebit = React.useCallback((entry: ExpenseEntry) => {
    return Number(entry.debit ?? 0) > 0 && Boolean(entry.beneficiaryId?.startsWith('employee-'));
  }, []);

  const bankRegIdFromEntry = React.useCallback((entry: ExpenseEntry) => {
    const m = /^bsi-(\d+)$/.exec(entry.id);
    return m ? Number(m[1]) : null;
  }, []);

  /** برداشت بانکی به کارمند که هنوز به مساعده همگام نشده (برای نمایش موقت) */
  const unsyncedPersonBankDebits = React.useMemo(
    () =>
      bankExpenses.filter((entry) => {
        if (!isEmployeeBankDebit(entry)) return false;
        const regId = bankRegIdFromEntry(entry);
        return regId == null || !syncedBsiRegistrationIds.has(regId);
      }),
    [bankExpenses, isEmployeeBankDebit, bankRegIdFromEntry, syncedBsiRegistrationIds],
  );

  /** گردش بانک بدون پرداخت‌های شخصی (آن‌ها در بخش مساعده می‌آیند) */
  const bankExpensesForLedger = React.useMemo(
    () => bankExpenses.filter((entry) => !isEmployeeBankDebit(entry)),
    [bankExpenses, isEmployeeBankDebit],
  );

  const allProjectMovements = React.useMemo(
    () => [...accountingExpenses, ...bankExpensesForLedger, ...advanceExpenses, ...unsyncedPersonBankDebits],
    [accountingExpenses, bankExpensesForLedger, advanceExpenses, unsyncedPersonBankDebits],
  );

  const beneficiaryFilterOptions = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of allProjectMovements) {
      const key = (entry.beneficiaryId || '').trim();
      if (!key) continue;
      const label = (entry.beneficiary || '').trim() || key;
      if (!map.has(key)) map.set(key, label);
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fa'));
  }, [allProjectMovements]);

  const ledgerMovements = React.useMemo(
    () =>
      filterExpenseRows(allProjectMovements, {
        filterTypeId,
        filterTypeLabel,
        filterBeneficiaryId,
        filterDateFrom,
        filterDateTo,
        filterDirection,
        filterAmountMin,
        filterAmountMax,
        filterDocNo,
        filterDescription,
        normalizeDate,
      }).sort((a, b) => normalizeDate(b.dateFa || '').localeCompare(normalizeDate(a.dateFa || ''))),
    [
      allProjectMovements,
      filterTypeId,
      filterTypeLabel,
      filterBeneficiaryId,
      filterDateFrom,
      filterDateTo,
      filterDirection,
      filterAmountMin,
      filterAmountMax,
      filterDocNo,
      filterDescription,
      normalizeDate,
    ],
  );

  const ledgerDeposits = ledgerMovements.reduce((sum, e) => sum + Number(e.credit ?? 0), 0);
  const ledgerWithdrawals = ledgerMovements.reduce((sum, e) => sum + Number(e.debit ?? 0), 0);

  const totalWithdrawals = allProjectMovements.reduce((sum, e) => sum + Number(e.debit ?? 0), 0);
  const totalDeposits = allProjectMovements.reduce((sum, e) => sum + Number(e.credit ?? 0), 0);
  const accountingWithdrawals = accountingExpenses.reduce((sum, e) => sum + Number(e.debit ?? 0), 0);
  const totalExpense =
    totalWithdrawals > 0
      ? totalWithdrawals
      : accountingWithdrawals > 0
        ? accountingWithdrawals
        : linesLoaded && accountingLines.length === 0 && bankStatements.length === 0 && projectAdvances.length === 0
          ? Number(project?.accounting_expense_total ?? 0)
          : 0;
  const contractAmount = Number(project?.final_amount ?? 0) || Number(project?.initial_estimate ?? 0);
  const grossProfit = contractAmount - totalExpense;
  const expenseRatio = contractAmount > 0 ? (totalExpense / contractAmount) * 100 : 0;
  const progressPercent = Math.max(0, Math.min(100, Number(project?.progress ?? 0)));
  const expectedFinalCost = progressPercent > 0 ? Math.round((totalExpense / progressPercent) * 100) : totalExpense;
  const expectedProfitAtCompletion = contractAmount - expectedFinalCost;

  const statusMeta = React.useCallback((status: string) => {
    const map: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
      planning: { label: 'برنامه ریزی', icon: <CircleDashed className="h-4 w-4" />, className: 'bg-slate-100 text-slate-700' },
      in_progress: { label: 'در حال اجرا', icon: <PlayCircle className="h-4 w-4" />, className: 'bg-sky-100 text-sky-700' },
      completed: { label: 'تکمیل شده', icon: <CheckCircle2 className="h-4 w-4" />, className: 'bg-emerald-100 text-emerald-700' },
      paused: { label: 'متوقف', icon: <PauseCircle className="h-4 w-4" />, className: 'bg-amber-100 text-amber-700' },
      cancelled: { label: 'لغو شده', icon: <XCircle className="h-4 w-4" />, className: 'bg-rose-100 text-rose-700' },
    };
    return map[status] ?? { label: 'نامشخص', icon: <CircleDashed className="h-4 w-4" />, className: 'bg-slate-100 text-slate-700' };
  }, []);

  const typeLabel = React.useCallback(
    (typeId: string) => {
      if (typeId.startsWith('advance:')) {
        const paymentType = typeId.slice('advance:'.length);
        if (paymentType === 'on_account') return 'علی‌الحساب';
        if (paymentType === 'other') return 'سایر پرداخت';
        return 'مساعده';
      }
      return expenseTypes.find((x) => String(x.id) === typeId)?.title ?? typeId;
    },
    [expenseTypes],
  );

  const handleFilterDateFromChange = React.useCallback(
    (value: string) => {
      if (value && filterDateTo) {
        const vn = normalizeDate(value);
        const tn = normalizeDate(filterDateTo);
        if (vn > tn) {
          setFilterDateError('تاریخ شروع نمی‌تواند از تاریخ پایان بزرگ‌تر باشد.');
          return;
        }
      }
      setFilterDateError('');
      setFilterDateFrom(value);
    },
    [filterDateTo, normalizeDate],
  );

  const applyLedgerDateFilter = React.useCallback((dateFa: string) => {
    const d = String(dateFa || '').trim();
    setFilterDateError('');
    setFilterDateFrom(d);
    setFilterDateTo(d);
  }, []);

  const handleFilterDateToChange = React.useCallback(
    (value: string) => {
      if (value && filterDateFrom) {
        const fn = normalizeDate(filterDateFrom);
        const vn = normalizeDate(value);
        if (fn > vn) {
          setFilterDateError('تاریخ پایان نمی‌تواند از تاریخ شروع کوچک‌تر باشد.');
          return;
        }
      }
      setFilterDateError('');
      setFilterDateTo(value);
    },
    [filterDateFrom, normalizeDate],
  );

  const paymentMeta = React.useCallback((method: ExpenseEntry['paymentMethod']) => {
    const map: Record<ExpenseEntry['paymentMethod'], { label: string; className: string }> = {
      بانک: { label: 'بانک', className: 'bg-sky-100 text-sky-700' },
      نقدی: { label: 'نقدی', className: 'bg-amber-100 text-amber-700' },
      تنخواه: { label: 'تنخواه', className: 'bg-violet-100 text-violet-700' },
    };
    return map[method];
  }, []);

  const columns = React.useMemo(
    () =>
      createProjectExpenseColumns({
        projectId,
        linkBeneficiary: !isBeneficiaryView,
        hideBeneficiaryColumn: isBeneficiaryView,
        accountTitleByCode,
        compact: true,
      }),
    [projectId, isBeneficiaryView, accountTitleByCode],
  );

  const beneficiaryPersonColumns = columns;

  const clearDocumentFilters = React.useCallback(() => {
    setFilterTypeId('');
    setFilterTypeLabel('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterBeneficiaryId('');
    setFilterDirection('');
    setFilterAmountMin('');
    setFilterAmountMax('');
    setFilterDocNo('');
    setFilterDescription('');
    setFilterDateError('');
  }, []);

  const filteredExpenses = React.useMemo(
    () =>
      filterExpenseRows(allProjectMovements, {
        filterTypeId,
        filterTypeLabel,
        filterBeneficiaryId,
        filterDateFrom,
        filterDateTo,
        filterDirection,
        filterAmountMin,
        filterAmountMax,
        filterDocNo,
        filterDescription,
        normalizeDate,
      }).sort((a, b) => normalizeDate(b.dateFa || '').localeCompare(normalizeDate(a.dateFa || ''))),
    [
      allProjectMovements,
      filterTypeId,
      filterTypeLabel,
      filterBeneficiaryId,
      filterDateFrom,
      filterDateTo,
      filterDirection,
      filterAmountMin,
      filterAmountMax,
      filterDocNo,
      filterDescription,
      normalizeDate,
    ],
  );

  const activeBeneficiary = React.useMemo(() => {
    if (!beneficiaryViewKey) return null;
    const fromLookup = beneficiaries.find((b) => b.id === beneficiaryViewKey);
    const fromRows = allProjectMovements.find((e) => e.beneficiaryId === beneficiaryViewKey);
    const parsed = parseCounterpartyKey(beneficiaryViewKey);
    return {
      key: beneficiaryViewKey,
      name: fromLookup?.name || fromRows?.beneficiary || beneficiaryViewKey,
      type: fromLookup?.type || parsed?.type || 'employee',
    };
  }, [beneficiaries, allProjectMovements, beneficiaryViewKey]);

  const beneficiaryRows = React.useMemo(() => {
    if (!beneficiaryViewKey) return [];
    return filterExpenseRows(
      allProjectMovements.filter((e) => e.beneficiaryId === beneficiaryViewKey),
      {
        filterTypeId,
        filterBeneficiaryId: '',
        filterDateFrom,
        filterDateTo,
        normalizeDate,
      },
    ).sort((a, b) => normalizeDate(b.dateFa || '').localeCompare(normalizeDate(a.dateFa || '')));
  }, [allProjectMovements, beneficiaryViewKey, filterTypeId, filterDateFrom, filterDateTo, normalizeDate]);

  const expenseByType = React.useMemo(() => {
    const map = new Map<string, number>();
    const labels = new Map<string, string>();
    filteredExpenses.filter((e) => Number(e.debit ?? 0) > 0).forEach((e) => {
      let key = e.expenseTypeId || '';
      let label = key ? typeLabel(key) : '';
      if (!key) {
        if (e.source === 'bsi_bank') {
          key = 'bsi-withdrawal';
          label = 'گردش بانک (برداشت)';
        } else {
          key = `doc-${e.documentId || e.id}`;
          label = e.docNo ? `سند حسابداری ${e.docNo}` : 'سند حسابداری';
          if (e.accountCode) label += ` · ${e.accountCode}`;
        }
      } else if (e.source === 'advance') {
        label = typeLabel(key);
      }
      map.set(key, (map.get(key) ?? 0) + Number(e.debit ?? 0));
      labels.set(key, label);
    });
    return Array.from(map.entries())
      .map(([k, v]) => ({ label: labels.get(k) ?? k, value: v }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [filteredExpenses, typeLabel]);

  const expenseByBeneficiary = React.useMemo(() => {
    const map = new Map<string, number>();
    filteredExpenses.filter((e) => Number(e.debit ?? 0) > 0).forEach((e) => {
      map.set(e.beneficiary || 'نامشخص', (map.get(e.beneficiary || 'نامشخص') ?? 0) + Number(e.debit ?? 0));
    });
    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [filteredExpenses]);

  const maxTypeValue = expenseByType[0]?.value ?? 1;
  const maxBeneficiaryValue = expenseByBeneficiary[0]?.value ?? 1;

  const openLedgerVoucherPrint = React.useCallback(
    (entry: ExpenseEntry) => {
      const credit = Number(entry.credit ?? 0);
      const debit = Number(entry.debit ?? entry.amount ?? 0);
      const isDeposit = credit > 0;
      const amount = isDeposit ? credit : debit;
      const sourceLabel =
        entry.source === 'bsi_bank'
          ? 'گردش بانک'
          : entry.source === 'advance'
            ? 'پرداخت / مساعده'
            : entry.source === 'accounting'
              ? 'سند حسابداری'
              : 'ثبت مالی';
      const expenseTypeTitle =
        entry.accountTitle ||
        (entry.expenseTypeId ? typeLabel(entry.expenseTypeId) : '') ||
        (isDeposit ? 'واریز' : 'پرداخت');

      saveProjectLedgerVoucherPayload({
        voucherNo: buildVoucherNo(entry),
        generatedAt: new Date().toLocaleString('fa-IR'),
        project: {
          id: projectId,
          label: project?.label ?? `پروژه ${projectId}`,
        },
        kind: isDeposit ? 'deposit' : 'withdrawal',
        kindLabel: isDeposit ? 'واریز' : 'برداشت',
        dateFa: entry.dateFa || '—',
        expenseTypeTitle,
        beneficiary: entry.beneficiary || '—',
        docNo: entry.docNo || '—',
        credit,
        debit: isDeposit ? 0 : debit,
        amount,
        amountInWords: amountToPersianWordsRial(amount) || '—',
        paymentMethod: entry.paymentMethod || '—',
        description: entry.description || '—',
        sourceLabel,
      });
      window.open(`/projects/${projectId}/ledger-voucher/print`, '_blank', 'noopener,noreferrer');
    },
    [project?.label, projectId, typeLabel],
  );

  const openBeneficiaryPrintCartex = React.useCallback(
    (beneficiaryKey: string, beneficiaryLabel: string) => {
      const rows = allProjectMovements.filter((x) => x.beneficiaryId === beneficiaryKey);
      saveProjectExpenseCartexPrintPayload({
        title: `کارتکس هزینه شخص - ${beneficiaryLabel}`,
        generatedAt: new Date().toLocaleString('fa-IR'),
        project: {
          id: projectId,
          label: project?.label ?? `پروژه ${projectId}`,
          status: project?.status ?? '',
          contractAmount,
        },
        filters: {
          beneficiaryLabel,
          typeLabel: filterTypeId ? typeLabel(filterTypeId) : undefined,
          dateFrom: filterDateFrom || filterDateTo || undefined,
          dateTo: filterDateTo || filterDateFrom || undefined,
        },
        rows: rows.map((r) => ({
          ...r,
          expenseTypeTitle: r.expenseTypeId ? typeLabel(r.expenseTypeId) : r.docNo ? `سند ${r.docNo}` : 'سند حسابداری',
          sourceBankLabel: '-',
          destinationBankLabel: '-',
        })),
        printOptions: { tableColumns: printColumns },
        summary: {
          count: rows.length,
          totalAmount: rows.reduce((sum, r) => sum + Number(r.amount || 0), 0),
        },
      });
      window.open(`/projects/${projectId}/expense-cartex/print`, '_blank', 'noopener,noreferrer');
    },
    [
      allProjectMovements,
      contractAmount,
      filterDateFrom,
      filterDateTo,
      filterTypeId,
      printColumns,
      project?.label,
      project?.status,
      projectId,
      typeLabel,
    ],
  );

  if (!loading && !project) {
    return (
      <div className="space-y-4">
        <PageHeader title="پروژه یافت نشد" description="شناسه پروژه معتبر نیست یا پروژه حذف شده است." />
        <Button asChild variant="outline">
          <Link to="/projects">بازگشت به لیست پروژه‌ها</Link>
        </Button>
      </div>
    );
  }

  if (section === 'register' || section === 'documents') {
    return <Navigate to={`/projects/${projectId}/overview`} replace />;
  }

  if (isBeneficiaryView) {
    if (!loading && activeBeneficiary && beneficiaryRows.length === 0 && allProjectMovements.length > 0) {
      const hasAnyForPerson = allProjectMovements.some((e) => e.beneficiaryId === beneficiaryViewKey);
      if (!hasAnyForPerson) {
        return <Navigate to={`/projects/${projectId}/overview`} replace />;
      }
    }
  } else if (!section || !PROJECT_WORKSPACE_SECTIONS.has(section)) {
    return <Navigate to={`/projects/${projectId}/overview`} replace />;
  }

  const workspaceDescription = isBeneficiaryView
    ? activeBeneficiary
      ? `واریزها و هزینه‌های ${activeBeneficiary.name} در این پروژه`
      : 'جزئیات طرف هزینه در این پروژه'
    : 'نمای خلاصه مالی، پیشرفت پیمان و سهم انواع هزینه و طرف‌ها برای همین پروژه.';

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title={project ? `پروژه: ${project.label}` : `پروژه ${projectId}`}
        description={workspaceDescription}
      />

      <LoadingLock loading={loading}>
        {loadError ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{loadError}</p>
        ) : null}

        {!isBeneficiaryView ? (
          <div className="-mt-2 flex flex-wrap gap-2 border-b border-slate-200/80 pb-4">
            <Button asChild variant="ghost" size="sm" className="text-slate-600">
              <Link to="/projects">لیست همهٔ پروژه‌ها</Link>
            </Button>
          </div>
        ) : (
          <nav className="-mt-2 flex flex-wrap gap-2 border-b border-slate-200/80 pb-4" aria-label="بخش‌های پروژه">
            <NavLink
              to={`/projects/${projectId}/overview`}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ring-1 ring-transparent',
                  isActive
                    ? 'bg-emerald-600 text-white shadow-sm ring-emerald-600/25'
                    : 'bg-white text-slate-700 shadow-sm ring-slate-200 hover:bg-slate-50 hover:ring-slate-300',
                )
              }
            >
              <LayoutDashboard className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
              خلاصهٔ مالی
            </NavLink>
            <Button asChild variant="ghost" size="sm" className="text-slate-600 sm:ms-auto">
              <Link to="/projects">لیست همهٔ پروژه‌ها</Link>
            </Button>
          </nav>
        )}

        {section === 'overview' ? (
          <ProjectWorkspaceOverviewTab
            project={project}
            contractAmount={contractAmount}
            totalExpense={totalExpense}
            totalDeposits={totalDeposits}
            totalWithdrawals={totalWithdrawals}
            ledgerMovements={ledgerMovements}
            ledgerDeposits={ledgerDeposits}
            ledgerWithdrawals={ledgerWithdrawals}
            beneficiaryFilterOptions={beneficiaryFilterOptions}
            filterBeneficiaryId={filterBeneficiaryId}
            setFilterBeneficiaryId={setFilterBeneficiaryId}
            filterDateFrom={filterDateFrom}
            filterDateTo={filterDateTo}
            handleFilterDateFromChange={handleFilterDateFromChange}
            handleFilterDateToChange={handleFilterDateToChange}
            applyLedgerDateFilter={applyLedgerDateFilter}
            filterDateError={filterDateError}
            filterDirection={filterDirection}
            setFilterDirection={setFilterDirection}
            filterAmountMin={filterAmountMin}
            setFilterAmountMin={setFilterAmountMin}
            filterAmountMax={filterAmountMax}
            setFilterAmountMax={setFilterAmountMax}
            filterTypeId={filterTypeId}
            setFilterTypeId={setFilterTypeId}
            filterTypeLabel={filterTypeLabel}
            setFilterTypeLabel={setFilterTypeLabel}
            filterDocNo={filterDocNo}
            setFilterDocNo={setFilterDocNo}
            filterDescription={filterDescription}
            setFilterDescription={setFilterDescription}
            clearLedgerFilters={clearDocumentFilters}
            advancesError={advancesError}
            onPrintVoucher={openLedgerVoucherPrint}
            canEditExpenseType={canEditLedgerExpenseType}
            onEditExpenseType={(entry) => {
              setExpenseTypeEditError(null);
              setEditingExpenseTypeEntry(entry);
            }}
            grossProfit={grossProfit}
            expenseRatio={expenseRatio}
            progressPercent={progressPercent}
            expectedFinalCost={expectedFinalCost}
            expectedProfitAtCompletion={expectedProfitAtCompletion}
            expenseByType={expenseByType}
            expenseByBeneficiary={expenseByBeneficiary}
            maxTypeValue={maxTypeValue}
            maxBeneficiaryValue={maxBeneficiaryValue}
            statusMeta={statusMeta}
          />
        ) : isBeneficiaryView && activeBeneficiary ? (
          <ProjectWorkspaceBeneficiaryTab
            projectId={projectId}
            projectLabel={project?.label ?? `پروژه ${projectId}`}
            beneficiaryKey={activeBeneficiary.key}
            beneficiaryName={activeBeneficiary.name}
            beneficiaryType={activeBeneficiary.type}
            rows={beneficiaryRows}
            columns={beneficiaryPersonColumns}
            expenseTypes={expenseTypes}
            filterTypeId={filterTypeId}
            setFilterTypeId={setFilterTypeId}
            filterDateFrom={filterDateFrom}
            filterDateTo={filterDateTo}
            handleFilterDateFromChange={handleFilterDateFromChange}
            handleFilterDateToChange={handleFilterDateToChange}
            filterDateError={filterDateError}
            clearFilters={clearDocumentFilters}
            onPrintCartex={() => openBeneficiaryPrintCartex(activeBeneficiary.key, activeBeneficiary.name)}
          />
        ) : null}
      </LoadingLock>

      <EditLedgerExpenseTypeModal
        open={Boolean(editingExpenseTypeEntry)}
        entry={editingExpenseTypeEntry}
        expenseTypes={expenseTypes}
        saving={savingExpenseType}
        error={expenseTypeEditError}
        onClose={() => {
          if (savingExpenseType) return;
          setEditingExpenseTypeEntry(null);
          setExpenseTypeEditError(null);
        }}
        onSave={(expenseTypeId) => {
          void saveLedgerExpenseType(expenseTypeId);
        }}
      />
    </div>
  );
};

export default ProjectDetailsPage;
